import React from "react";
import { Input } from "./Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";
import { Textarea } from "./Textarea";
import { Field, SectionHeading } from "./Field";
import { PrivacyFields } from "./PrivacyFields";
import { BaitPicker, BaitSelection } from "./BaitPicker";
import { RigPicker, RigSelection } from "./RigPicker";
import { SpotPicker } from "./SpotPicker";

const CARP_TYPES = ["Mirror", "Common", "Leather", "Grass", "Ghost", "Koi", "Unknown"];
const BAIT_UNITS = [
  "grams",
  "kilograms",
  "boilies",
  "handfuls",
  "PVA bags",
  "stringers",
];

export type CatchFormValue = {
  session_id: string;
  session_angler_id: string;
  peg: string;
  caught_at: string;
  species: string;
  carp_type: string;
  weight_lb: string;
  weight_oz: string;
  rod_number: string;
  rod_setup_id: string;
  spot: string;
  spot_id: string; // saved spot id, "" if none
  catch_species: string;
  distance_m: string;
  depth_ft: string;
  // canonical rig selection
  rig_selection: RigSelection;
  rig: string; // legacy snapshot fallback
  // feed bait
  feed_bait_selection: BaitSelection;
  main_bait: string; // legacy snapshot fallback
  // hookbait
  hookbait_selection: BaitSelection;
  hookbait: string; // legacy snapshot fallback
  additive_selection: BaitSelection;
  additive: string;
  baiting_approach: string;
  bait_amount: string;
  bait_unit: string;
  bait_notes: string;
  privacy_setting: string;
  share_peg_anonymously: boolean;
  catch_notes: string;
};

export function CatchFields({
  values,
  setValues,
  sessions,
  lockedSessionId,
  anglers,
  spots,
  rodSetups,
  onAnglerChange,
  compact = false,
}: {
  values: CatchFormValue;
  setValues: React.Dispatch<React.SetStateAction<CatchFormValue>>;
  sessions: any[];
  lockedSessionId?: string;
  anglers?: any[];
  /** Saved spots for the lake of the current session */
  spots?: any[];
  /** Current live rod setups for this session */
  rodSetups?: any[];
  /** Called after angler selection changes — parent uses this to fetch last-used spot */
  onAnglerChange?: (anglerId: string) => void;
  /** Compact mode is used by the live rod Caught action. It keeps only fish details visible. */
  compact?: boolean;
}) {
  const update = (key: keyof CatchFormValue, value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  const showAnglerPicker = anglers && anglers.length > 1;

  // Saved Spot = WHERE. Rod Setup = WHAT.
  // Legacy spot tackle defaults are only applied when no rod setup is selected,
  // so choosing a spot can never overwrite an explicit live rod setup.
  function applySpot(spotId: string, spot: any | null) {
    setValues((current) => {
      const useLegacyTackle = !current.rod_setup_id;
      return {
        ...current,
        spot_id: spotId,
        distance_m:
          spot?.distance_m != null ? String(spot.distance_m) : current.distance_m,
        depth_ft:
          spot?.depth_ft != null ? String(spot.depth_ft) : current.depth_ft,
        spot: spot?.name ?? current.spot,
        ...(useLegacyTackle && spot?.default_rig_snapshot
          ? {
              rig_selection: {
                canonical_id: spot.default_rig_id ?? null,
                user_rig_id: spot.default_user_rig_id ?? null,
                snapshot: spot.default_rig_snapshot,
              },
              rig: spot.default_rig_snapshot,
            }
          : {}),
        ...(useLegacyTackle && spot?.default_feed_bait_snapshot
          ? {
              feed_bait_selection: {
                canonical_id: spot.default_feed_bait_id ?? null,
                user_bait_id: spot.default_feed_user_bait_id ?? null,
                snapshot: spot.default_feed_bait_snapshot,
              },
              main_bait: spot.default_feed_bait_snapshot,
            }
          : {}),
        ...(useLegacyTackle && spot?.default_hookbait_snapshot
          ? {
              hookbait_selection: {
                canonical_id: spot.default_hookbait_id ?? null,
                user_bait_id: spot.default_hookbait_user_bait_id ?? null,
                snapshot: spot.default_hookbait_snapshot,
              },
              hookbait: spot.default_hookbait_snapshot,
            }
          : {}),
        ...(useLegacyTackle && spot?.baiting_approach
          ? { baiting_approach: spot.baiting_approach }
          : {}),
      };
    });
  }

  function applyRodSetup(setup: any) {
    setValues((current) => ({
      ...current,
      rod_number: String(setup.rod_number),
      rod_setup_id: String(setup.id),
      rig_selection: {
        canonical_id: setup.canonical_rig_id ?? null,
        user_rig_id: setup.user_rig_id ?? null,
        snapshot: setup.rig_snapshot ?? "",
      },
      rig: setup.rig_snapshot ?? "",
      feed_bait_selection: {
        canonical_id: setup.canonical_feed_bait_id ?? null,
        user_bait_id: setup.user_feed_bait_id ?? null,
        snapshot: setup.feed_bait_snapshot ?? "",
      },
      main_bait: setup.feed_bait_snapshot ?? "",
      hookbait_selection: {
        canonical_id: setup.canonical_hookbait_id ?? null,
        user_bait_id: setup.user_hookbait_id ?? null,
        snapshot: setup.hookbait_snapshot ?? "",
      },
      hookbait: setup.hookbait_snapshot ?? "",
      additive_selection: {
        canonical_id: setup.canonical_additive_bait_id ?? null,
        user_bait_id: setup.user_additive_bait_id ?? null,
        snapshot: setup.additive_snapshot ?? "",
      },
      additive: setup.additive_snapshot ?? "",
      baiting_approach: setup.baiting_approach ?? "",
      bait_amount:
        setup.bait_amount != null ? String(setup.bait_amount) : current.bait_amount,
      bait_unit: setup.bait_unit || current.bait_unit,
    }));
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <SectionHeading>Catch basics</SectionHeading>
        {!compact ? (
        <Field label="Session" id="catch-session">
          <Select
            value={values.session_id}
            disabled={Boolean(lockedSessionId)}
            onValueChange={(value) => {
              const session = sessions.find(
                (item) => String(item.id) === value,
              );
              setValues((current) => ({
                ...current,
                session_id: value,
                session_angler_id: "",
                rod_number: "",
                rod_setup_id: "",
                peg: current.peg || session?.peg || "",
              }));
            }}
          >
            <SelectTrigger
              id="catch-session"
              aria-label={
                lockedSessionId ? "Session locked to active session" : "Session"
              }
            >
              <SelectValue placeholder="Choose a session" />
            </SelectTrigger>
            <SelectContent>
              {sessions.map((session) => (
                <SelectItem key={session.id} value={String(session.id)}>
                  {session.lake_name} · {session.peg} ·{" "}
                  {new Date(session.start_at).toLocaleDateString("en-GB")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        ) : null}
        {!compact && lockedSessionId ? (
          <p className="text-xs text-secondary">
            Attached to your active session. The peg has been prefilled.
          </p>
        ) : null}
        {!compact && showAnglerPicker ? (
          <Field label="Caught by" id="catch-angler">
            <Select
              value={values.session_angler_id}
              onValueChange={(value) => {
                const angler = anglers!.find((a) => String(a.id) === value);
                setValues((current) => ({
                  ...current,
                  session_angler_id: value,
                  rod_number: "",
                  rod_setup_id: "",
                  rig_selection: { canonical_id: null, user_rig_id: null, snapshot: "" },
                  rig: "",
                  feed_bait_selection: { canonical_id: null, user_bait_id: null, snapshot: "" },
                  main_bait: "",
                  hookbait_selection: { canonical_id: null, user_bait_id: null, snapshot: "" },
                  hookbait: "",
                  additive_selection: { canonical_id: null, user_bait_id: null, snapshot: "" },
                  additive: "",
                  peg: angler?.peg_or_swim || "",
                  // Reset spot when angler changes; last-used will be loaded from server
                  spot_id: "",
                }));
                onAnglerChange?.(value);
              }}
            >
              <SelectTrigger id="catch-angler">
                <SelectValue placeholder="Choose angler" />
              </SelectTrigger>
              <SelectContent>
                {anglers!.map((angler) => (
                  <SelectItem key={angler.id} value={String(angler.id)}>
                    {angler.display_name}
                    {angler.is_guest ? " (Guest)" : ""}
                    {angler.peg_or_swim ? ` · ${angler.peg_or_swim}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ) : null}
        {/* Peg or swim is inherited from the parent session — not entered
            on Log Catch. The peg value is set by LogCatch from session.peg. */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Catch date and time"
            id="catch-time"
            help={compact ? "Defaults to now. Change it if you are logging the fish later." : undefined}
          >
            <Input
              id="catch-time"
              type="datetime-local"
              value={values.caught_at}
              onChange={(e) => update("caught_at", e.target.value)}
              required
            />
          </Field>
        </div>
        {/* Fish species (legacy free-text) is hidden — value is derived
            automatically from catch_species before save. See LogCatch/CatchDetails. */}
        <Field
          label="Species caught"
          id="catch-catch-species"
          help="For a Carp & Catfish session, choose what was actually caught."
        >
          <Select
            value={values.catch_species}
            required
            onValueChange={(value) =>
              setValues((current) => ({
                ...current,
                catch_species: value,
                carp_type: value === "carp" ? current.carp_type : "Unknown",
              }))
            }
          >
            <SelectTrigger
              id="catch-catch-species"
              aria-required="true"
              aria-invalid={!values.catch_species}
            >
              <SelectValue placeholder="Choose species caught" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Choose species caught</SelectItem>
              <SelectItem value="carp">Carp</SelectItem>
              <SelectItem value="catfish">Catfish</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        {values.catch_species === "carp" ? (
          <Field
            label="Carp type"
            id="catch-carp-type"
            help="Mirror, common, leather, grass, ghost or koi."
          >
            <Select
              value={values.carp_type}
              onValueChange={(value) => update("carp_type", value)}
            >
              <SelectTrigger id="catch-carp-type">
                <SelectValue placeholder="Choose carp type" />
              </SelectTrigger>
              <SelectContent>
                {CARP_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        ) : null}
        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Weight (lb)"
            id="catch-weight-lb"
            help="Optional. Leave both weight boxes blank if you have not weighed the fish yet."
          >
            <Input
              id="catch-weight-lb"
              type="number"
              min="0"
              value={values.weight_lb}
              onChange={(e) => update("weight_lb", e.target.value)}
              placeholder="30"
            />
          </Field>
          <Field label="Weight (oz)" id="catch-weight-oz">
            <Input
              id="catch-weight-oz"
              type="number"
              min="0"
              max="15"
              value={values.weight_oz}
              onChange={(e) => update("weight_oz", e.target.value)}
              placeholder="8"
            />
          </Field>
        </div>
      </div>
      {!compact ? (
      <>
      <div className="space-y-4">
        <SectionHeading>Rod and spot</SectionHeading>
        {spots && spots.length > 0 && (
          <SpotPicker
            spots={spots}
            value={values.spot_id}
            onChange={applySpot}
          />
        )}
        {rodSetups && rodSetups.length > 0 && (!showAnglerPicker || values.session_angler_id) ? (
          <div>
            <p className="mb-2 text-sm font-medium text-primary">Use saved rod setup</p>
            <div className="flex flex-wrap gap-2">
              {rodSetups
                .filter(
                  (setup: any) =>
                    !values.session_angler_id ||
                    String(setup.session_angler_id) === values.session_angler_id,
                )
                .map((setup: any) => (
                  <button
                    key={setup.id}
                    type="button"
                    onClick={() => applyRodSetup(setup)}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                      values.rod_setup_id === String(setup.id)
                        ? "border-accent bg-accent text-white"
                        : "border-border bg-background text-primary"
                    }`}
                  >
                    Rod {setup.rod_number}
                  </button>
                ))}
            </div>
            {values.rod_setup_id ? (
              <p className="mt-2 text-xs text-secondary">
                Setup loaded. Choosing a saved spot will only change location, distance and depth.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Rod number" id="catch-rod">
            <Input
              id="catch-rod"
              type="number"
              min="1"
              value={values.rod_number}
              onChange={(e) => setValues((current) => ({ ...current, rod_number: e.target.value, rod_setup_id: "" }))}
              placeholder="1"
            />
          </Field>
          <Field label="Distance (m)" id="catch-distance">
            <Input
              id="catch-distance"
              type="number"
              step="0.5"
              min="0"
              value={values.distance_m}
              onChange={(e) => update("distance_m", e.target.value)}
              placeholder="60"
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Fishing spot (description)" id="catch-spot">
            <Input
              id="catch-spot"
              value={values.spot}
              onChange={(e) => update("spot", e.target.value)}
              placeholder="Far margin, gravel bar"
            />
          </Field>
          <Field label="Depth (feet)" id="catch-depth">
            <Input
              id="catch-depth"
              type="number"
              step="0.1"
              min="0"
              value={values.depth_ft}
              onChange={(e) => update("depth_ft", e.target.value)}
              placeholder="8.5"
            />
          </Field>
        </div>
      </div>
      <div className="space-y-4">
        <SectionHeading>Rig and bait</SectionHeading>
        <RigPicker
          id="catch-rig"
          label="Rig"
          value={values.rig_selection}
          onChange={(sel) =>
            setValues((c) => ({ ...c, rig_selection: sel, rig: sel.snapshot, rod_setup_id: "" }))
          }
          speciesFilter={values.catch_species || "both"}
          placeholder="Search rigs… e.g. Ronnie, Chod"
        />
        <BaitPicker
          id="catch-feed-bait"
          label="Feed bait"
          value={values.feed_bait_selection}
          onChange={(sel) =>
            setValues((c) => ({
              ...c,
              feed_bait_selection: sel,
              main_bait: sel.snapshot,
              rod_setup_id: "",
            }))
          }
          speciesFilter={values.catch_species || "both"}
          presentationRole="feed"
          placeholder="Search baits… e.g. DNA The Bug"
        />
        <BaitPicker
          id="catch-hookbait"
          label="Hookbait"
          value={values.hookbait_selection}
          onChange={(sel) =>
            setValues((c) => ({
              ...c,
              hookbait_selection: sel,
              hookbait: sel.snapshot,
              rod_setup_id: "",
            }))
          }
          speciesFilter={values.catch_species || "both"}
          presentationRole="hookbait"
          placeholder="Search hookbaits… e.g. pop-up, wafter"
        />
        <BaitPicker
          id="catch-additive"
          label="Attractor / liquid / additive"
          value={values.additive_selection}
          onChange={(sel) =>
            setValues((c) => ({
              ...c,
              additive_selection: sel,
              additive: sel.snapshot,
              rod_setup_id: "",
            }))
          }
          speciesFilter={values.catch_species || "both"}
          presentationRole="additive"
          placeholder="e.g. Spicy Goo, SLK Smoke"
        />
        <Field label="Baiting approach" id="catch-baiting-approach">
          <Input
            id="catch-baiting-approach"
            value={values.baiting_approach}
            onChange={(e) => setValues((current) => ({ ...current, baiting_approach: e.target.value, rod_setup_id: "" }))}
            placeholder="e.g. light scatter, spod, stick mix"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Bait amount" id="catch-bait-amount">
            <Input
              id="catch-bait-amount"
              type="number"
              step="0.1"
              min="0"
              value={values.bait_amount}
              onChange={(e) => setValues((current) => ({ ...current, bait_amount: e.target.value, rod_setup_id: "" }))}
              placeholder="20"
            />
          </Field>
          <Field label="Bait unit" id="catch-bait-unit">
            <Select
              value={values.bait_unit}
              onValueChange={(value) => setValues((current) => ({ ...current, bait_unit: value, rod_setup_id: "" }))}
            >
              <SelectTrigger id="catch-bait-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BAIT_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field
          label="Bait notes"
          id="catch-bait-notes"
          help="Optional description, such as light scattering or 20 boilies."
        >
          <Input
            id="catch-bait-notes"
            value={values.bait_notes}
            onChange={(e) => update("bait_notes", e.target.value)}
            placeholder="e.g. light scattering"
          />
        </Field>
      </div>
      <div className="space-y-4">
        <SectionHeading>Privacy and sharing</SectionHeading>
        <PrivacyFields
          idPrefix="catch"
          privacy={values.privacy_setting}
          onPrivacyChange={(value) => update("privacy_setting", value)}
          sharePeg={values.share_peg_anonymously}
          onSharePegChange={(value) =>
            setValues((current) => ({
              ...current,
              share_peg_anonymously: value,
            }))
          }
        />
      </div>
      <div className="space-y-4">
        <SectionHeading>Notes</SectionHeading>
        <Field label="Catch notes" id="catch-notes">
          <Textarea
            id="catch-notes"
            rows={4}
            value={values.catch_notes}
            onChange={(e) => update("catch_notes", e.target.value)}
            placeholder="How did it happen?"
          />
        </Field>
      </div>
      </>
      ) : null}
    </div>
  );
}
